import type { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  InputDateTime,
  InputSelect,
  InputText,
  Loading,
  TableListData,
} from 'components';
import {
  Button,
  InputWraper,
  useForm,
  NumberUtils,
  Icon,
  IUseFormStructureItem,
} from 'modules';
import React, { useCallback, useState } from 'react';
import { useGetOrdersListQuery, useExportOrdersListQuery } from 'services';
import type { GetListOrderEntity, OrderInfoEntity } from 'services/openapi';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import {
  formatColumn,
  formatRowData,
  formatRowDataTotal,
} from 'configs/cell_xlsx';

import {
  convertSelectLeaderOptions,
  convertSelectStatusOptions,
  convertSelectOrderTypeOptions,
  convertVariables,
  type FilterType,
  getInitFilterValues,
  exportHeaders,
} from './config';
import { DEFAULT_PAGE } from 'configs';
import { format, startOfDay, endOfDay } from 'date-fns';
import { TransactionStatusColor } from 'configs/constants';
import { MainRouteKeys } from 'routes/enums';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

const Orders = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);

  const { data: leaders, isLoading } = { data: { data: [] }, isLoading: false };

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({ ...e, refreshData: variables.refreshData + 1 });
    },
    [variables],
  );

  const formatFromDate = (date: string | number) => {
    return startOfDay(new Date(date)).toUTCString();
  };

  const formatToDate = (date: string | number) => {
    return endOfDay(new Date(date)).toUTCString();
  };

  const initColumnsDefined = () => {
    const columnsDefined: ColumnDef<OrderInfoEntity>[] = [
      {
        accessorKey: 'orderCode',
        header: 'Mã đơn hàng',
        cell: (info) => {
          return (
            <a
              href={`${MainRouteKeys.TransactionBills}?orderCode=${info.row.original.orderCode}&fromDate=${formatFromDate(info.row.original.updatedAt)}&toDate=${formatToDate(info.row.original.updatedAt)}`}
            >
              {String(info.getValue())}
            </a>
          );
        },
      },
      {
        accessorKey: 'execPhoneNumber',
        header: 'Tài khoản thực hiện',
      },
      {
        accessorKey: 'leaderPhoneNumber',
        header: 'Trưởng nhóm',
      },
      {
        accessorKey: 'serviceType',
        header: 'Loại dịch vụ',
      },
      {
        accessorKey: 'orderSumTx',
        header: 'Lượng giao dịch',
      },
      {
        accessorKey: 'orderSumTxSuccess',
        header: 'Lượng thành công',
        cell: (info) => {
          return (
            <div>
              {info.getValue()
                ? NumberUtils.toFormatNumber(Number(info.getValue()))
                : '0'}
            </div>
          );
        },
      },
      {
        accessorKey: 'orderSumTxFailed',
        header: 'Lượng thất bại',
        cell: (info) => {
          return (
            <div>
              {info.getValue()
                ? NumberUtils.toFormatNumber(Number(info.getValue()))
                : '0'}
            </div>
          );
        },
      },
      {
        accessorKey: 'sumAmount',
        header: 'Số tiền',
        // cell: (info) => NumberUtils.toFormatNumber(Number(info.getValue())),
        cell: (info) => {
          return (
            <div className={'textInfo'}>
              {info.getValue()
                ? NumberUtils.toFormatNumber(Number(info.getValue()))
                : '0'}
            </div>
          );
        },
      },
      {
        accessorKey: 'sumPaymentAmount',
        header: 'Thanh toán',
        // cell: (info) => NumberUtils.toFormatNumber(Number(info.getValue())),
        cell: (info) => {
          return (
            <div className={'textInfo'}>
              {info.getValue()
                ? NumberUtils.toFormatNumber(Number(info.getValue()))
                : '0'}
            </div>
          );
        },
      },
      {
        accessorKey: 'sumRollbackAmount',
        header: 'Hoàn trả',
        // cell: (info) => NumberUtils.toFormatNumber(Number(info.getValue())),
        cell: (info) => {
          return (
            <div className={'textInfo'}>
              {info.getValue()
                ? NumberUtils.toFormatNumber(Number(info.getValue()))
                : '0'}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Trạng thái',
        cell: (info) => (
          <div
            className={
              TransactionStatusColor[
                info.getValue() as keyof typeof TransactionStatusColor
              ]
            }
          >
            {String(info.getValue())}
          </div>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Thời gian',
        cell: (info) =>
          format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy'),
      },
      {
        id: 'action',
        header: 'Thao tác',
        cell: (info) => {
          return (
            <Button
              type='button'
              buttonType='success'
              className='mr0 pdr10 pdl10 pdt0 pdb0'
              label={
                <a
                  href={`${MainRouteKeys.TransactionBills}?orderCode=${info.row.original.orderCode}&fromDate=${formatFromDate(info.row.original.updatedAt)}&endDate=${formatToDate(info.row.original.updatedAt)}`}
                >
                  <Icon.Eye />
                </a>
              }
            />
          );
        },
      },
    ];

    return columnsDefined;
  };

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'orderCode',
      label: 'Mã đơn hàng',
      defaultValue: initFilterValues.orderCode,
    },
    {
      name: 'leader',
      label: 'Trưởng nhóm',
      defaultValue: initFilterValues.leader,
    },
    {
      name: 'userExec',
      label: 'Tài khoản thực hiện',
      defaultValue: initFilterValues.userExec,
    },
    {
      name: 'fromDate',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: initFilterValues.fromDate,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.toDate).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.toDate);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'toDate',
      label: 'Ngày & giờ kết thúc',
      defaultValue: initFilterValues.toDate,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.fromDate).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.fromDate);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'status',
      label: 'Trạng thái',
      defaultValue: initFilterValues.status,
      validate: Yup.string().required('Vui lòng chọn Trạng thái'),
    },
    {
      name: 'serviceType',
      label: 'Loại dịch vụ',
      defaultValue: initFilterValues.serviceType,
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const { isFetching: isLoadingExport, refetch } = useExportOrdersListQuery({
    ...convertVariables(variables),
    page: '1',
    pageSize: String((dataTable as unknown as GetListOrderEntity)?.total),
  });

  const handleExportExcel = async () => {
    const allData = await refetch({
      ...convertVariables(variables),
      page: DEFAULT_PAGE,
      pageSize: String((dataTable as unknown as GetListOrderEntity)?.total),
    } as any);

    //
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Columns
    const headerColumn = exportHeaders.map((header) => {
      return {
        header: header,
        key: header,
        width: 40,
      };
    });
    worksheet.columns = headerColumn;

    const firstRow = worksheet.getRow(1);
    firstRow.eachCell((cell) => formatColumn(cell));

    // Data
    (Array.isArray(allData?.data?.data?.orders ?? [])
      ? (allData?.data?.data?.orders ?? [])
      : []
    ).map((item: OrderInfoEntity, index: number) => {
      const row = worksheet.addRow([
        index + 1,
        item.orderCode,
        item.execPhoneNumber,
        item.serviceType,
        item.orderSumTx,
        item.orderSumTxSuccess,
        item.orderSumTxFailed,
        NumberUtils.toFormatNumber(item.sumAmount),
        NumberUtils.toFormatNumber(item.sumPaymentAmount),
        NumberUtils.toFormatNumber(item.sumRollbackAmount),
        item.status,
        format(new Date(String(item.updatedAt)), 'HH:mm:ss dd/MM/yyyy'),
      ]);
      row.eachCell((cell) => formatRowData(cell));
    });

    // Total
    const row = worksheet.addRow([
      '',
      '',
      '',
      '',
      'TỔNG',
      `${allData.data?.data?.sumTx || 0}`,
      `${allData.data?.data?.sumTxSuccess || 0}`,
      `${allData.data?.data?.sumTxFailed || 0}`,
      `${NumberUtils.toFormatNumber(allData.data?.data?.sumAmount || 0)}`,
      `${NumberUtils.toFormatNumber(allData.data?.data?.sumPayAmount || 0)}`,
      `${NumberUtils.toFormatNumber(allData.data?.data?.sumRollBackAmount || 0)}`,
    ]);
    row.eachCell((cell, colNumber) => {
      if (
        colNumber === 1 ||
        colNumber === 2 ||
        colNumber === 3 ||
        colNumber === 4
      )
        return;
      formatRowDataTotal(cell);
    });

    // Generate Excel and trigger file download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const now = new Date();
    saveAs(
      blob,
      `Danh_sach_don_hang_${now.toLocaleTimeString('vn-VN', { hour12: false }).replace(/:/g, '_')}_${now.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '_')}.xlsx`,
    );
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('orderCode')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <Loading isLoading={isLoading}>
                <InputWraper
                  inputProps={getInputProps('leader')}
                  renderInput={(props) => (
                    <InputSelect
                      {...props}
                      options={convertSelectLeaderOptions(
                        (leaders?.data as any[]) || [],
                      )}
                    />
                  )}
                />
              </Loading>
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('userExec')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromDate')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toDate')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('status')}
                isRequired
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectStatusOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('serviceType')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectOrderTypeOptions()}
                  />
                )}
              />
            </div>
          </div>
          <div className='textCenter mt16 wrapButtonFilter'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='submit'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>
        </form>
      </Card>

      <TableListData<GetListOrderEntity>
        columns={initColumnsDefined() as ColumnDef<GetListOrderEntity>[]}
        useQuery={useGetOrdersListQuery}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        filterKey='searchDetailContent'
        dataQueryKey='orders'
        orderColumn
        callBackData={setDataTable}
        customTopRow={(data) => {
          if (data.data) {
            return (
              <>
                <tr>
                  <td colSpan={4}></td>
                  <td>
                    <strong>TỔNG</strong>
                  </td>
                  <td>
                    <strong>{data.data.sumTx}</strong>
                  </td>
                  <td>
                    <strong>{data.data.sumTxSuccess}</strong>
                  </td>
                  <td>
                    <strong>{data.data.sumTxFailed}</strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(data.data.sumAmount)}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(data.data.sumPayAmount)}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(data.data.sumRollBackAmount)}
                    </strong>
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </>
            );
          }
        }}
      />
    </>
  );
};

export default Orders;
